import React, { useEffect, useState, useCallback, useRef } from "react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  adminListUsers,
  adminListUserPosts,
  adminListGlobalPosts,
  adminCreatePost,
  adminDeletePost,
  uploadToR2,
} from "../../lib/studioApi";
import {
  Loader2,
  Paperclip,
  Music,
  Video,
  Image as ImageIcon,
  FileText,
  X,
  Send,
} from "lucide-react";
import { toast } from "sonner";
import PostCard from "../studio/PostCard";
import RichTextEditor from "../RichTextEditor";
import GoLiveButton from "./GoLiveButton";

const GLOBAL_ID = "__global__";
const MAX_SIZE_BY_KIND = {
  audio: 100 * 1024 * 1024,
  video: 500 * 1024 * 1024,
  image: 20 * 1024 * 1024,
  file: 100 * 1024 * 1024,
};

function makePendingId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function detectKind(file) {
  const t = (file.type || "").toLowerCase();
  if (t.startsWith("audio/")) return "audio";
  if (t.startsWith("video/")) return "video";
  if (t.startsWith("image/")) return "image";
  return "file";
}

function KindIcon({ kind, className = "w-4 h-4" }) {
  if (kind === "audio") return <Music className={className} />;
  if (kind === "video") return <Video className={className} />;
  if (kind === "image") return <ImageIcon className={className} />;
  return <FileText className={className} />;
}

function ComposeBox({ users, onPosted }) {
  const [targetId, setTargetId] = useState(GLOBAL_ID);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [pending, setPending] = useState([]); // {id, file, kind, progress, attachment?}
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const addFiles = (files) => {
    const next = [];
    for (const file of Array.from(files)) {
      const kind = detectKind(file);
      const max = MAX_SIZE_BY_KIND[kind];
      if (file.size > max) {
        toast.error(
          `${file.name} is too large. Max ${Math.round(max / (1024 * 1024))}MB for ${kind}`,
        );
        continue;
      }
      next.push({
        id: makePendingId(),
        file,
        kind,
        progress: 0,
        attachment: null,
      });
    }
    setPending((p) => [...p, ...next]);
    next.forEach((item) => uploadOne(item));
  };

  const uploadOne = async (item) => {
    try {
      const attachment = await uploadToR2(item.file, item.kind, {
        asAdmin: true,
        onProgress: (pct) => {
          setPending((p) =>
            p.map((x) => (x.id === item.id ? { ...x, progress: pct } : x)),
          );
        },
      });
      setPending((p) =>
        p.map((x) =>
          x.id === item.id
            ? { ...x, progress: 100, attachment: { ...attachment, kind: item.kind } }
            : x,
        ),
      );
    } catch (e) {
      toast.error(`Upload failed: ${item.file.name}`);
      setPending((p) => p.filter((x) => x.id !== item.id));
    }
  };

  const removePending = (id) => {
    setPending((p) => p.filter((x) => x.id !== id));
  };

  const submit = async () => {
    if (!targetId) return;
    if (!title.trim() && !body.trim() && pending.length === 0) {
      toast.error("Add a title, message, or attachment");
      return;
    }
    const stillUploading = pending.some((x) => !x.attachment);
    if (stillUploading) {
      toast.error("Wait for uploads to finish");
      return;
    }
    setSubmitting(true);
    try {
      await adminCreatePost({
        userId: targetId,
        title: title.trim(),
        body: body.trim(),
        attachments: pending.map((p) => p.attachment),
      });
      setTitle("");
      setBody("");
      setPending([]);
      toast.success(
        targetId === GLOBAL_ID
          ? "Announcement posted to everyone"
          : "Update posted",
      );
      onPosted();
    } catch (e) {
      toast.error("Post failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#0f1322] rounded-xl border border-white/5 shadow-sm p-5 mb-6">
      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4 mb-3">
        <div>
          <label className="text-xs font-semibold text-white/50 uppercase tracking-wider">
            Post to
          </label>
          <Select value={targetId} onValueChange={setTargetId}>
            <SelectTrigger
              data-testid="post-target-select"
              className="mt-1 bg-[#0b0f1e] border-white/10 text-white focus:ring-paul-yellow/40"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={GLOBAL_ID}>
                📢 Everyone (Global announcement)
              </SelectItem>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.artistName || u.name} — {u.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-semibold text-white/50 uppercase tracking-wider">
            Title (optional)
          </label>
          <Input
            data-testid="post-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Mix v3 — listen on good speakers"
            className="mt-1 bg-[#0b0f1e] border-white/10 text-white placeholder:text-white/30 focus-visible:border-paul-yellow/60 focus-visible:ring-paul-yellow/40"
          />
        </div>
      </div>

      <div className="mb-3">
        <label className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-1 block">
          Update
        </label>
        <RichTextEditor
          value={body}
          onChange={setBody}
          placeholder="Write an update for your artist…"
          theme="dark"
          minHeight="120px"
          testIdPrefix="post-body"
        />
      </div>

      {pending.length > 0 && (
        <div className="space-y-2 mb-3">
          {pending.map((p) => (
            <div
              key={p.id}
              className="flex items-center gap-3 bg-[#161a2c] rounded-lg p-2.5 border border-white/5"
            >
              <div className="w-8 h-8 rounded-md bg-paul-yellow/15 text-paul-yellow flex items-center justify-center flex-shrink-0">
                <KindIcon kind={p.kind} className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white truncate">
                  {p.file.name}
                </div>
                <div className="h-1.5 bg-white/10 rounded-full mt-1 overflow-hidden">
                  <div
                    className="h-full bg-paul-yellow transition-all"
                    style={{ width: `${p.progress}%` }}
                  />
                </div>
              </div>
              <div className="text-xs text-white/50 font-mono min-w-[35px] text-right">
                {p.progress}%
              </div>
              <button
                type="button"
                onClick={() => removePending(p.id)}
                className="text-white/40 hover:text-red-400"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between">
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          className="gap-2 border-white/15 bg-transparent text-white/80 hover:bg-white/10 hover:text-white"
          data-testid="post-attach-btn"
        >
          <Paperclip className="w-4 h-4" />
          Attach files
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            if (e.target.files) addFiles(e.target.files);
            e.target.value = "";
          }}
        />
        <Button
          data-testid="post-submit-btn"
          onClick={submit}
          disabled={submitting}
          className="bg-paul-yellow hover:bg-paul-yellow text-navy font-bold gap-2"
          style={{ color: "#0b1535" }}
        >
          {submitting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Send className="w-4 h-4" /> Post
            </>
          )}
        </Button>
      </div>
      <p className="text-[11px] text-white/40 mt-2">
        Audio ≤ 100MB · Video ≤ 500MB · Images ≤ 20MB · Other ≤ 100MB
      </p>
    </div>
  );
}

export default function StudioFeedPanel() {
  const [users, setUsers] = useState([]);
  const [view, setView] = useState(GLOBAL_ID); // global or userId
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadUsers = useCallback(async () => {
    try {
      setUsers(await adminListUsers());
    } catch (e) {
      console.error("Failed to load users:", e);
      toast.error("Failed to load users");
    }
  }, []);

  const loadPosts = useCallback(async () => {
    try {
      const data =
        view === GLOBAL_ID
          ? await adminListGlobalPosts()
          : await adminListUserPosts(view);
      setPosts(data);
    } catch (e) {
      console.error("Failed to load posts:", e);
      toast.error("Failed to load posts");
    } finally {
      setLoading(false);
    }
  }, [view]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const onDelete = async (post) => {
    if (!window.confirm("Delete this post? Files in R2 stay; only the post entry is removed."))
      return;
    try {
      await adminDeletePost(post.id);
      toast.success("Deleted");
      loadPosts();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="studio-feed-panel">
      <div className="mb-6">
        <h2 className="text-2xl font-extrabold text-white">Studio Feed</h2>
        <p className="text-white/50 text-sm mt-1">
          Post audio, video, files or text updates. Choose a specific artist or post a global announcement to everyone.
        </p>
      </div>

      <GoLiveButton users={users} />

      <ComposeBox users={users} onPosted={loadPosts} />

      <div className="flex items-center gap-3 mb-4">
        <label className="text-xs font-semibold text-white/50 uppercase tracking-wider">
          Viewing
        </label>
        <Select value={view} onValueChange={setView}>
          <SelectTrigger
            className="w-[320px] bg-[#10162a] border-white/10 text-white focus:ring-paul-yellow/40"
            data-testid="feed-view-select"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={GLOBAL_ID}>📢 Global announcements</SelectItem>
            {users.map((u) => (
              <SelectItem key={u.id} value={u.id}>
                {u.artistName || u.name} — {u.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-[#0b0f1e] border border-white/5 rounded-2xl p-5 md:p-6 text-white">
        {loading ? (
          <div className="text-center py-12 text-white/50">
            <Loader2 className="w-5 h-5 animate-spin inline-block" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-12 text-white/50 text-sm">
            No posts yet for this {view === GLOBAL_ID ? "channel" : "artist"}.
          </div>
        ) : (
          <div className="space-y-5">
            {posts.map((p) => (
              <PostCard key={p.id} post={p} onDelete={onDelete} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
